import { Sprite } from 'pixi.js';
import { TILE_HALF_H, TILE_HALF_W, Z_UNIT_PX } from '@enugu/shared';
import { animationFrame, WALK_SPEED } from '../character/Animation';
import { PaperDoll } from '../character/PaperDoll';
import { footprintAnchorOffset } from '../iso/IsoMath';
import { PathMover } from '../movement/PathMover';
import { AStar } from '../path/AStar';
import { FloorLayer } from '../render/FloorLayer';
import { WorldItem } from '../render/WorldItem';
import { WorldLayer } from '../render/WorldLayer';
import { TileGrid } from '../world/TileGrid';
export class Prop {
    def;
    item;
    sprite;
    constructor(def, item, sprite) {
        this.def = def;
        this.item = item;
        this.sprite = sprite;
    }
}
export class Wall {
    def;
    item;
    sprite;
    /** True if this wall hides a room interior behind it (cut in cutaway mode). */
    cutaway = false;
    low = false;
    constructor(def, item, sprite) {
        this.def = def;
        this.item = item;
        this.sprite = sprite;
    }
}
export class Character {
    id;
    mover;
    doll;
    item;
    name;
    anim = 'idle';
    animTime = 0;
    /** Interpolated render position (tile space). */
    rx;
    ry;
    goal = null;
    pathPending = false;
    /** Called once when the character reaches (or gives up on) its goal. */
    onArrive = null;
    constructor(id, mover, doll, item, name) {
        this.id = id;
        this.mover = mover;
        this.doll = doll;
        this.item = item;
        this.name = name;
        this.rx = mover.x;
        this.ry = mover.y;
    }
}
/** Character sort box half-size (tiles) and height (z units, ~80 px). */
const CHAR_R = 0.25;
const CHAR_H = 2.5;
/** Low (cut) wall height in z units (~12 px). */
const LOW_WALL_Z = 0.375;
function setSpriteTexture(s, tex) {
    s.texture = tex;
    const a = tex.defaultAnchor;
    if (a)
        s.anchor.set(a.x, a.y);
}
/**
 * One storey of one area: the walkability grid, floor, sorted world layer,
 * and the props, walls and characters in it. Owns pathfinding and the
 * per-tick path budget. Network-agnostic: game code (or, from M1, the net
 * layer) tells characters where to go; the scene never knows why.
 */
export class IsoScene {
    grid;
    astar;
    floor;
    world;
    props = [];
    walls = [];
    characters = [];
    /** Room id per tile (0 = outdoors). Drives wall cutaway. */
    rooms;
    wallMode = 'up';
    /** Path requests served per sim tick (quality setting). */
    pathBudgetPerTick = 6;
    tex;
    dollFrames;
    nextId = 1;
    pathQueue = [];
    /** Path searches run in the last tick (stat). */
    pathsLastTick = 0;
    p = { x: 0, y: 0 };
    constructor(opts) {
        this.grid = new TileGrid(opts.width, opts.height);
        this.astar = new AStar(this.grid);
        this.floor = new FloorLayer(opts.width, opts.height, opts.floor);
        this.world = new WorldLayer();
        this.rooms = new Uint16Array(opts.width * opts.height);
        this.tex = opts.texture;
        this.dollFrames = opts.dollFrames;
    }
    // ---------------------------------------------------------------- building
    markRoom(x, y, w, h, id) {
        for (let j = y; j < y + h; j++)
            for (let i = x; i < x + w; i++)
                if (this.grid.inBounds(i, j))
                    this.rooms[j * this.grid.width + i] = id;
    }
    roomAt(x, y) {
        return this.grid.inBounds(x, y) ? this.rooms[y * this.grid.width + x] : 0;
    }
    addProp(def) {
        const sprite = new Sprite({ texture: this.tex(def.texture) });
        const item = new WorldItem(this.nextId++, 'prop', sprite);
        const off = footprintAnchorOffset(def.w, def.h, this.p);
        const z = def.z ?? 0;
        const sx = (def.x - def.y) * TILE_HALF_W + off.x;
        const sy = (def.x + def.y) * TILE_HALF_H + off.y - z * Z_UNIT_PX;
        sprite.position.set(sx, sy);
        item.setBox(def.x, def.y, z, def.x + def.w, def.y + def.h, z + def.height);
        this.setRectFromSprite(item, sprite);
        item.pickable = def.pickable ?? true;
        const prop = new Prop(def, item, sprite);
        item.owner = prop;
        if (def.blocks ?? true)
            this.grid.blockFootprint(def.x, def.y, def.w, def.h, true);
        this.world.add(item);
        this.props.push(prop);
        return prop;
    }
    removeProp(prop) {
        const i = this.props.indexOf(prop);
        if (i < 0)
            return;
        this.props.splice(i, 1);
        if (prop.def.blocks ?? true)
            this.grid.blockFootprint(prop.def.x, prop.def.y, prop.def.w, prop.def.h, false);
        this.world.remove(prop.item);
        prop.sprite.destroy();
    }
    addWall(def) {
        const sprite = new Sprite({ texture: this.tex(`${def.style}/${def.edge}/${def.variant ?? 'plain'}`) });
        const item = new WorldItem(this.nextId++, 'wall', sprite);
        const wall = new Wall(def, item, sprite);
        item.owner = wall;
        // Anchor = bottom-centre of the zero-thickness footprint: N edge (1x0) or W edge (0x1).
        const off = def.edge === 'N' ? footprintAnchorOffset(1, 0, this.p) : footprintAnchorOffset(0, 1, this.p);
        sprite.position.set((def.x - def.y) * TILE_HALF_W + off.x, (def.x + def.y) * TILE_HALF_H + off.y);
        this.grid.setWall(def.x, def.y, def.edge, true);
        // Cutaway if the tile BEHIND the wall (smaller coordinate) is a room interior.
        const bx = def.edge === 'W' ? def.x - 1 : def.x;
        const by = def.edge === 'N' ? def.y - 1 : def.y;
        wall.cutaway = this.roomAt(bx, by) !== 0;
        this.applyWallShape(wall, this.isLow(wall));
        this.world.add(item);
        this.walls.push(wall);
        return wall;
    }
    isLow(w) {
        return this.wallMode === 'down' || (this.wallMode === 'cutaway' && w.cutaway);
    }
    applyWallShape(w, low) {
        w.low = low;
        const d = w.def;
        setSpriteTexture(w.sprite, this.tex(`${d.style}/${d.edge}/${low ? 'low' : (d.variant ?? 'plain')}`));
        const zTop = low ? LOW_WALL_Z : 3;
        if (d.edge === 'N')
            w.item.setBox(d.x, d.y, 0, d.x + 1, d.y, zTop);
        else
            w.item.setBox(d.x, d.y, 0, d.x, d.y + 1, zTop);
        this.setRectFromSprite(w.item, w.sprite);
    }
    /** Sims-style wall display: up (full), cutaway (walls hiding interiors are cut), down (all cut). */
    setWallMode(mode) {
        this.wallMode = mode;
        for (const w of this.walls) {
            const low = this.isLow(w);
            if (low !== w.low)
                this.applyWallShape(w, low);
        }
    }
    setRectFromSprite(item, s) {
        const t = s.texture;
        const w = t.orig.width;
        const h = t.orig.height;
        const left = s.x - s.anchor.x * w;
        const top = s.y - s.anchor.y * h;
        item.setRect(left, top, left + w, top + h);
    }
    addCharacter(def) {
        const doll = new PaperDoll(this.dollFrames, def.outfit);
        const mover = new PathMover(def.x, def.y, def.speed ?? WALK_SPEED);
        const item = new WorldItem(this.nextId++, 'character', doll.view);
        const ch = new Character(item.id, mover, doll, item, def.name ?? `char${item.id}`);
        item.owner = ch;
        doll.setPose('idle', 'SE', 0);
        this.updateCharacterView(ch);
        this.world.add(item);
        this.characters.push(ch);
        return ch;
    }
    removeCharacter(ch) {
        const i = this.characters.indexOf(ch);
        if (i < 0)
            return;
        this.characters.splice(i, 1);
        const q = this.pathQueue.indexOf(ch);
        if (q >= 0)
            this.pathQueue.splice(q, 1);
        this.world.remove(ch.item);
        ch.doll.destroy();
    }
    // ------------------------------------------------------------- movement
    /**
     * Asks a character to walk to a tile. `immediate` paths are computed now
     * (the local player: instant feedback); others queue under the per-tick
     * budget so 200 wanderers never spike a frame.
     */
    moveTo(ch, gx, gy, immediate = false) {
        ch.goal = { x: gx, y: gy };
        if (immediate) {
            this.computePath(ch);
            return;
        }
        if (!ch.pathPending) {
            ch.pathPending = true;
            this.pathQueue.push(ch);
        }
    }
    computePath(ch) {
        ch.pathPending = false;
        const goal = ch.goal;
        if (!goal)
            return;
        const o = ch.mover.pathOrigin();
        const path = this.astar.findPath(o.x, o.y, goal.x, goal.y, { allowPartial: true, maxExpansions: 4000 });
        this.pathsLastTick++;
        if (path && path.length > 0) {
            ch.mover.setPath(path);
        }
        else if (!ch.mover.moving) {
            // Already there, or nowhere to go.
            const reached = o.x === goal.x && o.y === goal.y;
            ch.goal = null;
            ch.onArrive?.(ch, reached);
        }
    }
    /** Fixed-rate simulation step. */
    fixedUpdate(dt) {
        this.pathsLastTick = 0;
        let budget = this.pathBudgetPerTick;
        while (budget-- > 0 && this.pathQueue.length > 0) {
            const ch = this.pathQueue.shift();
            this.computePath(ch);
        }
        const grid = this.grid;
        for (let i = 0; i < this.characters.length; i++) {
            const ch = this.characters[i];
            const m = ch.mover;
            const wasMoving = m.moving;
            m.step(dt, grid);
            if (m.blocked) {
                // A dynamic obstacle appeared: re-path from where we stand.
                m.blocked = false;
                if (ch.goal)
                    this.moveTo(ch, ch.goal.x, ch.goal.y);
            }
            else if (wasMoving && !m.moving && ch.goal && !ch.pathPending) {
                const reached = m.tileX === ch.goal.x && m.tileY === ch.goal.y;
                ch.goal = null;
                ch.onArrive?.(ch, reached);
            }
        }
    }
    // ------------------------------------------------------------- rendering
    updateCharacterView(ch) {
        const x = ch.rx;
        const y = ch.ry;
        const sx = (x - y) * TILE_HALF_W;
        const sy = (x + y) * TILE_HALF_H;
        ch.doll.view.position.set(sx, sy);
        ch.item.setBox(x - CHAR_R, y - CHAR_R, 0, x + CHAR_R, y + CHAR_R, CHAR_H);
        // Visible body of the 64x128 frame around the feet (vision §6: feet at 32,120; ~80 px tall).
        ch.item.setRect(sx - 20, sy - 86, sx + 20, sy + 6);
    }
    /**
     * Per display frame: interpolate characters, animate the on-screen ones,
     * then cull + depth-sort the world and cull floor chunks.
     */
    renderUpdate(alpha, frameDt, view) {
        const chars = this.characters;
        for (let i = 0; i < chars.length; i++) {
            const ch = chars[i];
            const m = ch.mover;
            ch.rx = m.prevX + (m.x - m.prevX) * alpha;
            ch.ry = m.prevY + (m.y - m.prevY) * alpha;
            this.updateCharacterView(ch);
            const anim = m.moving || m.x !== m.prevX || m.y !== m.prevY ? 'walk' : 'idle';
            if (anim !== ch.anim) {
                ch.anim = anim;
                ch.animTime = 0;
            }
            else {
                ch.animTime += frameDt;
            }
            const it = ch.item;
            // Animate only what can be seen (same test the world layer uses).
            if (it.right > view.left && it.left < view.right && it.bottom > view.top && it.top < view.bottom) {
                ch.doll.setPose(anim, m.facing, animationFrame(anim, ch.animTime));
            }
        }
        this.world.update(view);
    }
}
